import type { PinpointMappingType } from '@/features/comment/models/pinpoint';
import { PROJECT_BASE_DIR } from '@/features/project/review-sheet/utils/constants';
import { generateQueryString } from '@/utils';
import { matchProjectDetailPath } from '@/utils/route-match';
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { usePinpoint } from '../components/pinpoint';
import { getQueryParamsByField } from '../utils/activity-tab-mapping-by-pipoint-field';

export const useNavigateToPinpoint = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentParams = Object.fromEntries(searchParams.entries());
  const { scrollToPinpoint, setPinpoint } = usePinpoint();

  return useCallback(
    async ({
      objectId,
      pinpoint,
      newTab = false,
      commentId,
    }: {
      objectId: string;
      pinpoint?: PinpointMappingType | null | undefined;
      newTab?: boolean;
      commentId: string;
    }) => {
      const schedulePinpointUpdate = (
        delay: number,
        pinpoint?: PinpointMappingType | null,
      ) => {
        if (pinpoint) {
          return setTimeout(() => {
            setPinpoint(pinpoint);
          }, delay);
        }
        return undefined;
      };

      // projectページ内でのリンク
      const queryString = buildQueryStringForPinpoint({
        pinpoint: pinpoint || undefined,
        currentParams,
        commentId: commentId ?? undefined,
      });

      if (matchProjectDetailPath(location.pathname)) {
        openOrNavigate(
          `${PROJECT_BASE_DIR}/${objectId}${queryString}`,
          newTab,
          navigate,
        );

        if (pinpoint) {
          scrollToPinpoint(pinpoint);
        }

        // NOTE: Wait for the page to transition before setting the pinpoint.
        const timer = schedulePinpointUpdate(300, pinpoint);
        return () => clearTimeout(timer);
      }

      openOrNavigate(
        `${PROJECT_BASE_DIR}/${objectId}${queryString}`,
        newTab,
        navigate,
      );
      if (pinpoint) {
        scrollToPinpoint(pinpoint);
      }

      // NOTE: 違うページからプロジェクトへの遷移時のみ、うまくスクロールされない可能性を下げるために遅らせる秒数を少し増やす。
      const timer = schedulePinpointUpdate(500, pinpoint);
      return () => clearTimeout(timer);
    },
    [navigate, setPinpoint, currentParams, scrollToPinpoint],
  );
};

const removeUndefined = <T extends Record<string, any>>(obj: T) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined || v !== null),
  ) as Partial<T>;

const buildQueryStringForPinpoint = ({
  pinpoint,
  currentParams,
  commentId,
}: {
  pinpoint?: PinpointMappingType;
  currentParams?: Record<string, string>;
  commentId?: string;
}) => {
  const sceneParams = getQueryParamsByField(pinpoint);

  const mergedParams = removeUndefined({
    ...currentParams,
    ...sceneParams,
    commentId,
  });

  return generateQueryString(mergedParams);
};

const openOrNavigate = async (
  path: string,
  newTab: boolean,
  navigate: ReturnType<typeof useNavigate>,
) => {
  if (newTab) {
    window.open(path, '_blank');
  } else {
    await navigate(path);
  }
};
